/**
 * DocumentTemplateEngine - Legal document templates and automated generation
 * 
 * Provides comprehensive legal document templates, automated document
 * generation, and intelligent content suggestions for legal practice.
 */

import { LocalDocumentState, AttorneyInfo, LegalDocumentType, Jurisdiction, TextRange } from '../types/DocumentTypes';

interface DocumentTemplate {
  templateId: string;
  name: string;
  category: TemplateCategory;
  jurisdiction: string;
  practiceArea: string;
  description: string;
  content: TemplateContent;
  variables: TemplateVariable[];
  formatting: TemplateFormatting;
  metadata: TemplateMetadata;
  usage: TemplateUsage;
}

interface TemplateContent {
  sections: TemplateSection[];
  boilerplate: string[];
  requiredElements: string[];
  optionalElements: string[];
  placeholders: { [key: string]: string };
}

interface TemplateSection {
  sectionId: string;
  title: string;
  content: string;
  isRequired: boolean;
  order: number;
  variables: string[];
  subsections?: TemplateSection[];
}

interface TemplateVariable {
  variableId: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'selection' | 'attorney' | 'client';
  description: string;
  defaultValue?: any;
  required: boolean;
  validation?: VariableValidation;
  options?: string[]; // For selection type
}

interface VariableValidation {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}

interface TemplateFormatting {
  jurisdiction: string;
  documentType: string;
  pageSetup: any; // References PageSetupOptions
  styles: { [selector: string]: any };
}

interface TemplateMetadata {
  author: string;
  created: Date;
  modified: Date;
  version: string;
  tags: string[];
  jurisdiction: string;
  practiceArea: string;
  complexity: 'simple' | 'intermediate' | 'advanced';
  estimatedTime: number; // Minutes to complete
}

interface TemplateUsage {
  timesUsed: number;
  lastUsed?: Date;
  averageCompletionTime: number;
  userRating?: number;
  feedback: TemplateFeedback[];
}

interface TemplateFeedback {
  userId: string;
  rating: number;
  comment: string;
  date: Date;
}

type TemplateCategory = 
  | 'pleadings'
  | 'motions'
  | 'briefs'
  | 'contracts'
  | 'correspondence'
  | 'discovery'
  | 'estate_planning'
  | 'corporate'
  | 'litigation'
  | 'employment'
  | 'real_estate'
  | 'family_law'
  | 'criminal'
  | 'intellectual_property'
  | 'administrative'
  | 'other';

interface DocumentGenerationOptions {
  templateId: string;
  variables: { [variableId: string]: any };
  attorney: AttorneyInfo;
  client?: ClientInfo;
  matter?: MatterInfo;
  includeBoilerplate: boolean;
  applyFormatting: boolean;
  validateContent: boolean;
}

interface ClientInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  type: 'individual' | 'corporation' | 'partnership' | 'llc' | 'other';
}

interface MatterInfo {
  matterNumber: string;
  description: string;
  practiceArea: string;
  jurisdiction: string;
  court?: string;
  caseNumber?: string;
}

export class DocumentTemplateEngine {
  private templates: Map<string, DocumentTemplate> = new Map();
  private templateCategories: Map<TemplateCategory, DocumentTemplate[]> = new Map();
  private recentTemplates: string[] = [];
  private favoriteTemplates: Set<string> = new Set();
  
  // Configuration
  private maxRecentTemplates: number = 20;
  private enableTemplateAnalytics: boolean = true;
  private enableContentSuggestions: boolean = true;
  private validationTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeLegalTemplates();
    this.initializeTemplateCategories();
  }

  // ============================================================================
  // DOCUMENT GENERATION
  // ============================================================================

  /**
   * Generate document from template with variable substitution
   */
  async generateDocument(options: DocumentGenerationOptions): Promise<{
    success: boolean;
    documentState: LocalDocumentState;
    warnings: string[];
    errors: string[];
    generatedSections: string[];
    usedVariables: string[];
  }> {
    console.log(`[DocumentTemplateEngine] Generating document from template: ${options.templateId}`);
    
    const template = this.templates.get(options.templateId);
    if (!template) {
      return {
        success: false,
        documentState: this.createEmptyDocumentState(),
        warnings: [],
        errors: [`Template not found: ${options.templateId}`],
        generatedSections: [],
        usedVariables: []
      };
    }
    
    // Validate variables
    const variableValidation = this.validateTemplateVariables(template, options.variables);
    if (!variableValidation.valid) {
      return {
        success: false,
        documentState: this.createEmptyDocumentState(),
        warnings: variableValidation.warnings,
        errors: variableValidation.errors,
        generatedSections: [],
        usedVariables: []
      };
    }
    
    // Generate content
    const generationResult = await this.generateTemplateContent(template, options);
    
    // Create document state
    const documentState = await this.createDocumentStateFromTemplate(
      generationResult.content,
      template,
      options
    );
    
    // Update template usage
    this.updateTemplateUsage(options.templateId);
    
    return {
      success: true,
      documentState,
      warnings: generationResult.warnings,
      errors: [],
      generatedSections: generationResult.sections,
      usedVariables: Object.keys(options.variables)
    };
  }

  /**
   * Generate template content with variable substitution
   */
  private async generateTemplateContent(
    template: DocumentTemplate,
    options: DocumentGenerationOptions
  ): Promise<{
    content: string;
    sections: string[];
    warnings: string[];
  }> {
    const sections: string[] = [];
    const warnings: string[] = [];
    let content = '';
    
    // Generate each section
    for (const section of template.content.sections) {
      if (section.isRequired || this.shouldIncludeSection(section, options)) {
        const sectionContent = await this.generateSection(section, options.variables, options);
        
        content += sectionContent + '\n\n';
        sections.push(section.title);
      }
    }
    
    // Add boilerplate if requested
    if (options.includeBoilerplate) {
      const boilerplate = this.generateBoilerplate(template, options);
      content += boilerplate;
    }
    
    // Perform variable substitution
    content = this.performVariableSubstitution(content, options.variables, options);
    
    // Add attorney signature block
    content += this.generateSignatureBlock(options.attorney);
    
    return { content, sections, warnings };
  }

  /**
   * Generate individual template section
   */
  private async generateSection(
    section: TemplateSection,
    variables: { [key: string]: any },
    options: DocumentGenerationOptions
  ): Promise<string> {
    let sectionContent = section.content;
    
    // Generate subsections if any
    if (section.subsections) {
      for (const subsection of section.subsections) {
        const subsectionContent = await this.generateSection(subsection, variables, options);
        sectionContent += '\n' + subsectionContent;
      }
    }
    
    // Apply section-specific variable substitution
    for (const variableId of section.variables) {
      if (variables[variableId] !== undefined) {
        const placeholder = `{{${variableId}}}`;
        sectionContent = sectionContent.replace(
          new RegExp(placeholder, 'g'),
          variables[variableId]
        );
      }
    }
    
    return sectionContent;
  }

  // ============================================================================
  // VARIABLE PROCESSING
  // ============================================================================

  /**
   * Validate template variables
   */
  private validateTemplateVariables(
    template: DocumentTemplate,
    variables: { [key: string]: any }
  ): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Check required variables
    for (const templateVar of template.variables) {
      if (templateVar.required && variables[templateVar.variableId] === undefined) {
        errors.push(`Required variable missing: ${templateVar.name}`);
      }
      
      // Validate variable format
      if (variables[templateVar.variableId] !== undefined) {
        const validation = this.validateVariable(templateVar, variables[templateVar.variableId]);
        if (!validation.valid) {
          errors.push(...validation.errors);
          warnings.push(...validation.warnings);
        }
      }
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateVariable(
    templateVar: TemplateVariable,
    value: any
  ): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Type validation
    switch (templateVar.type) {
      case 'text':
        if (typeof value !== 'string') {
          errors.push(`${templateVar.name} must be text`);
        } else if (templateVar.validation) {
          const validation = templateVar.validation;
          
          if (validation.minLength && value.length < validation.minLength) {
            errors.push(`${templateVar.name} must be at least ${validation.minLength} characters`);
          }
          
          if (validation.maxLength && value.length > validation.maxLength) {
            errors.push(`${templateVar.name} must be no more than ${validation.maxLength} characters`);
          }
          
          if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
            errors.push(`${templateVar.name} format is invalid`);
          }
        }
        break;
        
      case 'number':
        if (typeof value !== 'number') {
          errors.push(`${templateVar.name} must be a number`);
        } else if (templateVar.validation) {
          const validation = templateVar.validation;
          
          if (validation.min !== undefined && value < validation.min) {
            errors.push(`${templateVar.name} must be at least ${validation.min}`);
          }
          
          if (validation.max !== undefined && value > validation.max) {
            errors.push(`${templateVar.name} must be no more than ${validation.max}`);
          }
        }
        break;
        
      case 'date':
        if (!(value instanceof Date) && typeof value !== 'string') {
          errors.push(`${templateVar.name} must be a valid date`);
        }
        break;
        
      case 'selection':
        if (templateVar.options && !templateVar.options.includes(value)) {
          errors.push(`${templateVar.name} must be one of: ${templateVar.options.join(', ')}`);
        }
        break;
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Perform variable substitution in content
   */
  private performVariableSubstitution(
    content: string,
    variables: { [key: string]: any },
    options: DocumentGenerationOptions
  ): string {
    let substituted = content;
    
    // Substitute template variables
    for (const [variableId, value] of Object.entries(variables)) {
      const placeholder = `{{${variableId}}}`;
      substituted = substituted.replace(new RegExp(placeholder, 'g'), String(value));
    }
    
    // Substitute built-in variables
    substituted = this.substituteBuiltInVariables(substituted, options);
    
    // Clean up any remaining placeholders
    substituted = substituted.replace(/\{\{[^}]+\}\}/g, '[MISSING]');
    
    return substituted;
  }

  private substituteBuiltInVariables(
    content: string,
    options: DocumentGenerationOptions
  ): string {
    const now = new Date();
    const attorney = options.attorney;
    
    const builtInVariables: { [key: string]: string } = {
      'CURRENT_DATE': now.toLocaleDateString(),
      'CURRENT_YEAR': now.getFullYear().toString(),
      'ATTORNEY_NAME': attorney.name,
      'ATTORNEY_TITLE': attorney.title || 'Attorney',
      'ATTORNEY_BAR_NUMBER': attorney.barNumber || '',
      'ATTORNEY_FIRM': attorney.firm || '',
      'ATTORNEY_ADDRESS': attorney.address || '',
      'ATTORNEY_PHONE': attorney.phone || '',
      'ATTORNEY_EMAIL': attorney.email || '',
      'CLIENT_NAME': options.client?.name || '[CLIENT NAME]',
      'CLIENT_ADDRESS': options.client?.address || '[CLIENT ADDRESS]',
      'MATTER_NUMBER': options.matter?.matterNumber || '[MATTER NUMBER]',
      'CASE_NUMBER': options.matter?.caseNumber || '[CASE NUMBER]',
      'COURT_NAME': options.matter?.court || '[COURT NAME]'
    };
    
    let substituted = content;
    for (const [variable, value] of Object.entries(builtInVariables)) {
      const placeholder = `{{${variable}}}`;
      substituted = substituted.replace(new RegExp(placeholder, 'g'), value);
    }
    
    return substituted;
  }

  // ============================================================================
  // TEMPLATE MANAGEMENT
  // ============================================================================

  /**
   * Get templates by category and jurisdiction
   */
  getTemplates(
    category?: TemplateCategory,
    jurisdiction?: string,
    practiceArea?: string
  ): DocumentTemplate[] {
    let templates = Array.from(this.templates.values());
    
    if (category) {
      templates = templates.filter(t => t.category === category);
    }
    
    if (jurisdiction) {
      templates = templates.filter(t => t.jurisdiction === jurisdiction || t.jurisdiction === 'all');
    }
    
    if (practiceArea) {
      templates = templates.filter(t => t.practiceArea === practiceArea || t.practiceArea === 'general');
    }
    
    // Sort by usage and rating
    return templates.sort((a, b) => {
      const aScore = (a.usage.timesUsed * 0.7) + ((a.usage.userRating || 0) * 0.3);
      const bScore = (b.usage.timesUsed * 0.7) + ((b.usage.userRating || 0) * 0.3);
      return bScore - aScore;
    });
  }

  /**
   * Get template by ID
   */
  getTemplate(templateId: string): DocumentTemplate | null {
    return this.templates.get(templateId) || null;
  }

  /**
   * Search templates by keyword
   */
  searchTemplates(query: string): DocumentTemplate[] {
    const searchTerm = query.toLowerCase();
    const results: DocumentTemplate[] = [];
    
    for (const template of this.templates.values()) {
      if (this.matchesSearchQuery(template, searchTerm)) {
        results.push(template);
      }
    }
    
    // Sort by relevance
    return results.sort((a, b) => {
      const aRelevance = this.calculateSearchRelevance(a, searchTerm);
      const bRelevance = this.calculateSearchRelevance(b, searchTerm);
      return bRelevance - aRelevance;
    });
  }

  private matchesSearchQuery(template: DocumentTemplate, searchTerm: string): boolean {
    const searchFields = [
      template.name,
      template.description,
      template.practiceArea,
      template.jurisdiction,
      ...template.metadata.tags
    ].map(field => field.toLowerCase());
    
    return searchFields.some(field => field.includes(searchTerm));
  }

  private calculateSearchRelevance(template: DocumentTemplate, searchTerm: string): number {
    let relevance = 0;
    
    // Exact name match
    if (template.name.toLowerCase().includes(searchTerm)) relevance += 10;
    
    // Description match
    if (template.description.toLowerCase().includes(searchTerm)) relevance += 5;
    
    // Practice area match
    if (template.practiceArea.toLowerCase().includes(searchTerm)) relevance += 7;
    
    // Tag match
    if (template.metadata.tags.some(tag => tag.toLowerCase().includes(searchTerm))) relevance += 3;
    
    // Usage bonus
    relevance += Math.min(template.usage.timesUsed * 0.1, 5);
    
    return relevance;
  }

  // ============================================================================
  // TEMPLATE INITIALIZATION
  // ============================================================================

  private initializeLegalTemplates(): void {
    // Federal Court Motion to Dismiss
    this.addTemplate({
      templateId: 'federal_motion_to_dismiss',
      name: 'Motion to Dismiss (Federal)',
      category: 'motions',
      jurisdiction: 'federal',
      practiceArea: 'litigation',
      description: 'Federal court motion to dismiss under FRCP 12(b)(6)',
      content: {
        sections: [
          {
            sectionId: 'caption',
            title: 'Caption',
            content: 'IN THE UNITED STATES DISTRICT COURT\nFOR THE {{DISTRICT}}\n\n{{PLAINTIFF_NAME}},\n\nPlaintiff,\n\nv.\n\n{{DEFENDANT_NAME}},\n\nDefendant.\n\nCase No. {{CASE_NUMBER}}',
            isRequired: true,
            order: 1,
            variables: ['DISTRICT', 'PLAINTIFF_NAME', 'DEFENDANT_NAME', 'CASE_NUMBER']
          },
          {
            sectionId: 'title',
            title: 'Motion Title',
            content: 'MOTION TO DISMISS UNDER FEDERAL RULE OF CIVIL PROCEDURE 12(b)(6)',
            isRequired: true,
            order: 2,
            variables: []
          },
          {
            sectionId: 'introduction',
            title: 'Introduction',
            content: 'TO THE HONORABLE COURT:\n\nDefendant {{DEFENDANT_NAME}}, by and through undersigned counsel, respectfully moves this Court to dismiss Plaintiff\'s Complaint pursuant to Federal Rule of Civil Procedure 12(b)(6) for failure to state a claim upon which relief can be granted.',
            isRequired: true,
            order: 3,
            variables: ['DEFENDANT_NAME']
          },
          {
            sectionId: 'statement_of_facts',
            title: 'Statement of Facts',
            content: '{{FACTUAL_BACKGROUND}}',
            isRequired: true,
            order: 4,
            variables: ['FACTUAL_BACKGROUND']
          },
          {
            sectionId: 'argument',
            title: 'Argument',
            content: 'I. THE COMPLAINT FAILS TO STATE A CLAIM UPON WHICH RELIEF CAN BE GRANTED\n\n{{LEGAL_ARGUMENT}}',
            isRequired: true,
            order: 5,
            variables: ['LEGAL_ARGUMENT']
          },
          {
            sectionId: 'conclusion',
            title: 'Conclusion',
            content: 'For the foregoing reasons, Defendant respectfully requests that this Court grant this Motion to Dismiss and dismiss Plaintiff\'s Complaint {{WITH_OR_WITHOUT_PREJUDICE}}.',
            isRequired: true,
            order: 6,
            variables: ['WITH_OR_WITHOUT_PREJUDICE']
          }
        ],
        boilerplate: [
          'This motion is made and based upon the pleadings, records, and files herein.',
          'Oral argument is respectfully requested.'
        ],
        requiredElements: ['Caption', 'Motion Title', 'Legal Argument'],
        optionalElements: ['Certificate of Service'],
        placeholders: {}
      },
      variables: [
        {
          variableId: 'DISTRICT',
          name: 'Federal District',
          type: 'selection',
          description: 'Federal judicial district',
          required: true,
          options: ['CENTRAL DISTRICT OF CALIFORNIA', 'SOUTHERN DISTRICT OF NEW YORK', 'DISTRICT OF COLUMBIA']
        },
        {
          variableId: 'PLAINTIFF_NAME',
          name: 'Plaintiff Name',
          type: 'text',
          description: 'Name of the plaintiff',
          required: true,
          validation: { minLength: 2, maxLength: 200 }
        },
        {
          variableId: 'DEFENDANT_NAME',
          name: 'Defendant Name',
          type: 'text',
          description: 'Name of the defendant',
          required: true,
          validation: { minLength: 2, maxLength: 200 }
        },
        {
          variableId: 'CASE_NUMBER',
          name: 'Case Number',
          type: 'text',
          description: 'Court case number',
          required: true,
          validation: { pattern: '^[0-9]+-[A-Z]+-[0-9]+$' }
        },
        {
          variableId: 'FACTUAL_BACKGROUND',
          name: 'Factual Background',
          type: 'text',
          description: 'Relevant factual background',
          required: true,
          validation: { minLength: 50 }
        },
        {
          variableId: 'LEGAL_ARGUMENT',
          name: 'Legal Argument',
          type: 'text',
          description: 'Legal argument for dismissal',
          required: true,
          validation: { minLength: 100 }
        },
        {
          variableId: 'WITH_OR_WITHOUT_PREJUDICE',
          name: 'Prejudice',
          type: 'selection',
          description: 'Dismissal with or without prejudice',
          required: true,
          options: ['with prejudice', 'without prejudice', 'without prejudice and with leave to amend']
        }
      ],
      formatting: {
        jurisdiction: 'federal',
        documentType: 'motion',
        pageSetup: {},
        styles: {}
      },
      metadata: {
        author: 'EdgeLex',
        created: new Date(),
        modified: new Date(),
        version: '1.0',
        tags: ['motion', 'dismiss', 'frcp', '12b6'],
        jurisdiction: 'federal',
        practiceArea: 'litigation',
        complexity: 'intermediate',
        estimatedTime: 45
      },
      usage: {
        timesUsed: 0,
        averageCompletionTime: 45,
        feedback: []
      }
    });
    
    // California Complaint Template
    this.addTemplate({
      templateId: 'california_complaint',
      name: 'Complaint (California)',
      category: 'pleadings',
      jurisdiction: 'california',
      practiceArea: 'litigation',
      description: 'California state court complaint with line numbering',
      content: {
        sections: [
          {
            sectionId: 'caption',
            title: 'Caption',
            content: 'SUPERIOR COURT OF CALIFORNIA\nCOUNTY OF {{COUNTY}}\n\n{{PLAINTIFF_NAME}},\n\nPlaintiff,\n\nv.\n\n{{DEFENDANT_NAME}},\n\nDefendant.\n\nCase No. {{CASE_NUMBER}}',
            isRequired: true,
            order: 1,
            variables: ['COUNTY', 'PLAINTIFF_NAME', 'DEFENDANT_NAME', 'CASE_NUMBER']
          },
          {
            sectionId: 'parties',
            title: 'Parties',
            content: '1. Plaintiff {{PLAINTIFF_NAME}} is {{PLAINTIFF_DESCRIPTION}}.\n\n2. Defendant {{DEFENDANT_NAME}} is {{DEFENDANT_DESCRIPTION}}.',
            isRequired: true,
            order: 2,
            variables: ['PLAINTIFF_NAME', 'PLAINTIFF_DESCRIPTION', 'DEFENDANT_NAME', 'DEFENDANT_DESCRIPTION']
          },
          {
            sectionId: 'jurisdiction',
            title: 'Jurisdiction and Venue',
            content: '3. This Court has jurisdiction over this matter pursuant to {{JURISDICTION_BASIS}}.\n\n4. Venue is proper in this County pursuant to {{VENUE_BASIS}}.',
            isRequired: true,
            order: 3,
            variables: ['JURISDICTION_BASIS', 'VENUE_BASIS']
          }
        ],
        boilerplate: [],
        requiredElements: ['Caption', 'Parties', 'Jurisdiction'],
        optionalElements: ['Prayer for Damages'],
        placeholders: {}
      },
      variables: [
        {
          variableId: 'COUNTY',
          name: 'County',
          type: 'selection',
          description: 'California county',
          required: true,
          options: ['LOS ANGELES', 'SAN FRANCISCO', 'ORANGE', 'SAN DIEGO', 'SACRAMENTO']
        }
        // Additional variables would be defined here...
      ],
      formatting: {
        jurisdiction: 'california',
        documentType: 'pleading',
        pageSetup: {},
        styles: {}
      },
      metadata: {
        author: 'EdgeLex',
        created: new Date(),
        modified: new Date(),
        version: '1.0',
        tags: ['complaint', 'pleading', 'california'],
        jurisdiction: 'california',
        practiceArea: 'litigation',
        complexity: 'intermediate',
        estimatedTime: 60
      },
      usage: {
        timesUsed: 0,
        averageCompletionTime: 60,
        feedback: []
      }
    });
    
    // Contract Template
    this.addTemplate({
      templateId: 'service_agreement',
      name: 'Service Agreement',
      category: 'contracts',
      jurisdiction: 'all',
      practiceArea: 'corporate',
      description: 'Professional services agreement template',
      content: {
        sections: [
          {
            sectionId: 'title',
            title: 'Title',
            content: 'PROFESSIONAL SERVICES AGREEMENT',
            isRequired: true,
            order: 1,
            variables: []
          },
          {
            sectionId: 'parties',
            title: 'Parties',
            content: 'This Professional Services Agreement ("Agreement") is entered into on {{CONTRACT_DATE}} between {{CLIENT_NAME}}, a {{CLIENT_ENTITY_TYPE}} ("Client"), and {{SERVICE_PROVIDER_NAME}}, a {{PROVIDER_ENTITY_TYPE}} ("Service Provider").',
            isRequired: true,
            order: 2,
            variables: ['CONTRACT_DATE', 'CLIENT_NAME', 'CLIENT_ENTITY_TYPE', 'SERVICE_PROVIDER_NAME', 'PROVIDER_ENTITY_TYPE']
          },
          {
            sectionId: 'services',
            title: 'Services',
            content: 'Service Provider agrees to provide the following services: {{SERVICES_DESCRIPTION}}',
            isRequired: true,
            order: 3,
            variables: ['SERVICES_DESCRIPTION']
          }
        ],
        boilerplate: [
          'This Agreement constitutes the entire agreement between the parties.',
          'This Agreement shall be governed by the laws of {{GOVERNING_LAW}}.'
        ],
        requiredElements: ['Parties', 'Services', 'Compensation'],
        optionalElements: ['Termination', 'Confidentiality'],
        placeholders: {}
      },
      variables: [
        {
          variableId: 'CONTRACT_DATE',
          name: 'Contract Date',
          type: 'date',
          description: 'Date the contract is signed',
          required: true
        },
        {
          variableId: 'CLIENT_NAME',
          name: 'Client Name',
          type: 'text',
          description: 'Name of the client',
          required: true
        }
        // Additional variables...
      ],
      formatting: {
        jurisdiction: 'all',
        documentType: 'contract',
        pageSetup: {},
        styles: {}
      },
      metadata: {
        author: 'EdgeLex',
        created: new Date(),
        modified: new Date(),
        version: '1.0',
        tags: ['contract', 'services', 'agreement'],
        jurisdiction: 'all',
        practiceArea: 'corporate',
        complexity: 'simple',
        estimatedTime: 30
      },
      usage: {
        timesUsed: 0,
        averageCompletionTime: 30,
        feedback: []
      }
    });
    
    console.log(`[DocumentTemplateEngine] Initialized ${this.templates.size} templates`);
  }

  private initializeTemplateCategories(): void {
    // Organize templates by category
    for (const template of this.templates.values()) {
      if (!this.templateCategories.has(template.category)) {
        this.templateCategories.set(template.category, []);
      }
      this.templateCategories.get(template.category)!.push(template);
    }
    
    console.log(`[DocumentTemplateEngine] Organized templates into ${this.templateCategories.size} categories`);
  }

  // ============================================================================
  // CONTENT GENERATION HELPERS
  // ============================================================================

  private shouldIncludeSection(
    section: TemplateSection,
    options: DocumentGenerationOptions
  ): boolean {
    // Logic to determine if optional section should be included
    // Based on variables provided, matter type, etc.
    return true; // Placeholder
  }

  private generateBoilerplate(
    template: DocumentTemplate,
    options: DocumentGenerationOptions
  ): string {
    return template.content.boilerplate.join('\n\n') + '\n\n';
  }

  private generateSignatureBlock(attorney: AttorneyInfo): string {
    return `\n\nRespectfully submitted,\n\n_________________________\n${attorney.name}\n${attorney.title || 'Attorney'}\n${attorney.barNumber ? `Bar No. ${attorney.barNumber}` : ''}\n${attorney.firm}\n${attorney.address}\n${attorney.phone}\n${attorney.email}`;
  }

  private async createDocumentStateFromTemplate(
    content: string,
    template: DocumentTemplate,
    options: DocumentGenerationOptions
  ): Promise<LocalDocumentState> {
    return {
      text: content,
      caretPosition: 0,
      selection: null,
      version: 1,
      isDirty: true,
      created: new Date(),
      modified: new Date(),
      attorney: options.attorney,
      documentType: 'document' as LegalDocumentType,
      jurisdiction: 'federal' as Jurisdiction,
      lastServerSync: 0,
      serverVersion: 0,
      dirtyRegions: new Set()
    };
  }

  private createEmptyDocumentState(): LocalDocumentState {
    return {
      text: '',
      caretPosition: 0,
      selection: null,
      version: 1,
      isDirty: false,
      created: new Date(),
      modified: new Date(),
      attorney: {
        id: 'default',
        name: 'Unknown Attorney',
        email: '',
        firmName: '',
        firm: '',
        barNumber: '',
        role: 'associate' as const,
        seniority: 0,
        jurisdiction: 'federal',
        title: 'Attorney',
        phone: '',
        address: ''
      },
      documentType: 'document' as LegalDocumentType,
      jurisdiction: 'federal' as Jurisdiction,
      lastServerSync: 0,
      serverVersion: 0,
      dirtyRegions: new Set()
    };
  }

  // ============================================================================
  // TEMPLATE USAGE TRACKING
  // ============================================================================

  private updateTemplateUsage(templateId: string): void {
    const template = this.templates.get(templateId);
    if (template) {
      template.usage.timesUsed++;
      template.usage.lastUsed = new Date();
      
      // Update recent templates
      this.addToRecentTemplates(templateId);
    }
  }

  private addToRecentTemplates(templateId: string): void {
    // Remove if already exists
    const index = this.recentTemplates.indexOf(templateId);
    if (index >= 0) {
      this.recentTemplates.splice(index, 1);
    }
    
    // Add to front
    this.recentTemplates.unshift(templateId);
    
    // Maintain limit
    if (this.recentTemplates.length > this.maxRecentTemplates) {
      this.recentTemplates = this.recentTemplates.slice(0, this.maxRecentTemplates);
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get recent templates
   */
  getRecentTemplates(): DocumentTemplate[] {
    return this.recentTemplates
      .map(id => this.templates.get(id))
      .filter(Boolean) as DocumentTemplate[];
  }

  /**
   * Get favorite templates
   */
  getFavoriteTemplates(): DocumentTemplate[] {
    return Array.from(this.favoriteTemplates)
      .map(id => this.templates.get(id))
      .filter(Boolean) as DocumentTemplate[];
  }

  /**
   * Add template to favorites
   */
  addToFavorites(templateId: string): void {
    this.favoriteTemplates.add(templateId);
  }

  /**
   * Remove template from favorites
   */
  removeFromFavorites(templateId: string): void {
    this.favoriteTemplates.delete(templateId);
  }

  /**
   * Get template categories
   */
  getTemplateCategories(): TemplateCategory[] {
    return Array.from(this.templateCategories.keys());
  }

  /**
   * Get templates in category
   */
  getTemplatesByCategory(category: TemplateCategory): DocumentTemplate[] {
    return this.templateCategories.get(category) || [];
  }

  /**
   * Get template variables for form generation
   */
  getTemplateVariables(templateId: string): TemplateVariable[] {
    const template = this.templates.get(templateId);
    return template ? template.variables : [];
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private addTemplate(template: DocumentTemplate): void {
    this.templates.set(template.templateId, template);
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy template engine
   */
  destroy(): void {
    this.templates.clear();
    this.templateCategories.clear();
    
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    
    console.log('[DocumentTemplateEngine] Destroyed');
  }
}
